// Selection context — applies the drill-down to the Mapbox map (US-878).
//
// Mirrors the US-875 split: the selection STATE lives in UIContext (the drill
// path + pure actions); this provider is where that state is APPLIED to the map
// and where the map talks back. It owns, all behind `safe*` guards so a missing
// admin tileset degrades gracefully (R-10):
//   - idempotent creation of the country source + per-level hit / selection
//     layers, placed under the style's labels (R-1/R-2);
//   - the `map.on("click")` handler that queries the active level's hit layer,
//     derives the unit, and calls `selectFeature` (R-1/R-2);
//   - an effect that, on selection change, clears + re-applies the ISO gap
//     filter on VISIBLE layers, highlights the unit (ADB exception aside),
//     syncs the China-clipped layers and frames the unit (R-3/R-4/R-5/R-6);
//   - re-application of the active filter when a layer is toggled on, via the
//     `map-layer-visibility-change` seam LayersContext emits (R-3).
// Consume through the `useSelection` hook. MapContext stays map-only (R-8).
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import useMap from "../hooks/useMap";
import useUI from "../hooks/useUI";
import useLayers from "../hooks/useLayers";
import {
  ALL_BOUNDARY_LAYERS,
  APAC_HOME,
  BORDER_EXCEPTION_ISO3,
  CHINA_LAYER_DRIVERS,
  COUNTRY_CODE_FILTERED_LAYERS,
  COUNTRY_SOURCE,
  ISO_FILTERED_LAYERS,
  LEVELS,
  LEVEL_ORDER,
  PROP_KEYS,
  SELECTABLE_ISO3,
  SELECTION_COLOR,
  boundaryLayersForLevel,
  isSelectableIso3,
  isoNumFor,
  shouldHighlightBorder,
} from "../config/selectionConfig";
import { dmcName } from "../config/dmcConfig";
import { colors } from "../theme/tokens";

export const SelectionContext = createContext(null);

// — Pure geometry helper (replaces @turf/bbox; zero new deps) ————————————————
// Walk any GeoJSON geometry's coordinates into a [[w,s],[e,n]] bbox — the exact
// shape map.fitBounds accepts. Returns null for empty/degenerate geometry.
const geometryBounds = (geometry) => {
  if (!geometry || !geometry.coordinates) return null;
  const b = [
    [Infinity, Infinity],
    [-Infinity, -Infinity],
  ];
  const walk = (coords) => {
    if (typeof coords[0] === "number") {
      const [lng, lat] = coords;
      if (lng < b[0][0]) b[0][0] = lng;
      if (lat < b[0][1]) b[0][1] = lat;
      if (lng > b[1][0]) b[1][0] = lng;
      if (lat > b[1][1]) b[1][1] = lat;
      return;
    }
    coords.forEach(walk);
  };
  walk(geometry.coordinates);
  return Number.isFinite(b[0][0]) ? b : null;
};

// — Guarded map accessors (David's style may omit a configured id, R-10) ————
const hasLayer = (map, id) => !!(map.getLayer && map.getLayer(id));
const safeSetFilter = (map, id, filter) => {
  if (map.setFilter && hasLayer(map, id)) map.setFilter(id, filter);
};
const safeSetVisibility = (map, id, visibility) => {
  if (map.setLayoutProperty && hasLayer(map, id)) {
    map.setLayoutProperty(id, "visibility", visibility);
  }
};
const isLayerVisible = (map, id) => {
  if (!hasLayer(map, id) || !map.getLayoutProperty) return false;
  return map.getLayoutProperty(id, "visibility") !== "none";
};
const getLayerMeta = (map, id) => {
  const l = map.getLayer && map.getLayer(id);
  return l && l.source ? { source: l.source, sourceLayer: l["source-layer"] } : null;
};
const firstSymbolLayerId = (map) => {
  const style = map.getStyle && map.getStyle();
  return (style?.layers || []).find((l) => l.type === "symbol")?.id;
};
// Add the layer (or move it) directly under the first label so highlights/hit
// targets sit above the gap fills but below place labels (legacy pattern).
const ensureLayerUnderLabels = (map, def) => {
  if (!map.addLayer) return;
  const before = firstSymbolLayerId(map);
  if (!hasLayer(map, def.id)) {
    map.addLayer(def, before);
  } else if (before && map.moveLayer) {
    try {
      map.moveLayer(def.id, before);
    } catch {
      /* layer order is best-effort */
    }
  }
};

// — Country focus mask (US-920 · R-4) ————————————————————————————————————————
// Rasters have no attributes, so the per-country clip that filters the vector
// layers cannot apply to them. The focus effect is an INVERTED VEIL instead: a
// semi-opaque fill over every country EXCEPT the selection, drawn above the
// raster layers and below the selection/hover chrome and labels. It follows
// the same exception rule as the clips (isoNum == null → ADB border-exception
// or no selection → veil off). The worldview clause keeps duplicate
// disputed-variant polygons from stacking double-dark veils. NOTE: the veil
// exposes boundary rendering choices — visually verify disputed borders in
// the QA pass; swapping the mask onto the ADB-compliant ADM0 source is a
// data-only change if needed.
const FOCUS_MASK_ID = "country-focus-mask";
const MASK_MATCH_NOTHING = ["==", COUNTRY_SOURCE.isoProp, ""];
const maskFilterFor = (iso3) => [
  "all",
  ["!=", ["get", COUNTRY_SOURCE.isoProp], iso3],
  ["any", ["==", ["get", "worldview"], "all"], ["in", "US", ["get", "worldview"]]],
];

// Reset every highlight layer to its empty filter — the clean slate each apply
// starts from before re-highlighting the current unit.
const clearHighlights = (map) => {
  safeSetFilter(map, "country-selection-fill", ["==", COUNTRY_SOURCE.isoProp, ""]);
  safeSetFilter(map, "country-selection-line", ["==", COUNTRY_SOURCE.isoProp, ""]);
  safeSetFilter(map, "admin1-selection-line", ["==", PROP_KEYS.id, ""]);
  safeSetFilter(map, "admin2-selection-line", ["==", PROP_KEYS.id, ""]);
};

// — Hover affordance (demo UX) ————————————————————————————————————————————————
// Per-level hover layers: a lighter, dashed "preview" of what a click will select,
// paired with a cursor that tells selectable (pointer) from non-selectable
// (not-allowed) apart. Same selectability gate as the click handler. ADB
// border-exception units (shouldHighlightBorder false) draw no outline even on
// hover — matching their no-highlight selected state (D3/R-5) — while still
// getting the pointer cursor that signals they ARE selectable.
const HOVER = {
  country: {
    key: COUNTRY_SOURCE.isoProp,
    fill: "country-hover-fill",
    casing: "country-hover-line-casing",
    line: "country-hover-line",
  },
  provinces: {
    key: PROP_KEYS.id,
    casing: "admin1-hover-line-casing",
    line: "admin1-hover-line",
  },
  districts: {
    key: PROP_KEYS.id,
    casing: "admin2-hover-line-casing",
    line: "admin2-hover-line",
  },
};

// Reset every hover layer to its empty filter — the clean slate on mouseleave, a
// level switch, or a change of hovered unit. Guarded, so absent admin hover
// layers simply skip (R-10).
const clearHoverLayers = (map) => {
  Object.values(HOVER).forEach((h) => {
    if (h.fill) safeSetFilter(map, h.fill, ["==", h.key, ""]);
    safeSetFilter(map, h.casing, ["==", h.key, ""]);
    safeSetFilter(map, h.line, ["==", h.key, ""]);
  });
};

// Set the map canvas cursor. Guarded for the test fake map, which may not expose
// getCanvas.
const setMapCursor = (map, value) => {
  const canvas = map.getCanvas && map.getCanvas();
  if (canvas && canvas.style) canvas.style.cursor = value;
};

// A base layer counts as ON when its layout is visible AND, for the raster
// gap layers (US-920: always layout-visible, toggled via raster-opacity),
// its opacity is above zero. Vector bases have no raster-opacity — treated
// as on by layout alone.
const isBaseLayerOn = (map, id) => {
  if (!isLayerVisible(map, id)) return false;
  try {
    const opacity = map.getPaintProperty && map.getPaintProperty(id, "raster-opacity");
    if (typeof opacity === "number") return opacity > 0;
  } catch {
    /* non-raster layer or fake map — layout visibility decides */
  }
  return true;
};

// China-clipped layers stay visible only when nothing is selected or the
// selection is an exception country, AND their driving base layer is on (R-6),
// AND their China raster twin is not already painting (US-920 R-11) — when
// the twin renders China in Continuous mode, re-showing the vector companion
// would stack aggregate blocks over the continuous surface. If the twin is
// absent (stale pre-republish TileJSON, R-10) the companion keeps covering
// China: graceful degradation, not a regression.
const syncChinaLayerVisibility = (map, iso3) => {
  const keepVisible = !iso3 || BORDER_EXCEPTION_ISO3.has(iso3);
  CHINA_LAYER_DRIVERS.forEach(({ china, chnRaster, bases }) => {
    const baseOn = bases.some((b) => isBaseLayerOn(map, b));
    const twinPainting = !!chnRaster && isBaseLayerOn(map, chnRaster);
    safeSetVisibility(
      map,
      china,
      keepVisible && baseOn && !twinPainting ? "visible" : "none"
    );
  });
};

// Build the breadcrumb crumbs from a drill path. Root is always present; the
// last crumb is the current location. Earlier crumbs carry the drill-up target.
const buildCrumbs = (selection) => {
  const crumbs = [{ key: "apac", label: "Asia-Pacific", level: null }];
  if (!selection) return crumbs;
  crumbs.push({ key: "country", label: selection.name, level: "country" });
  if (selection.level === "provinces" || selection.level === "districts") {
    crumbs.push({
      key: "provinces",
      label: selection.admin1?.name || "Province",
      level: "provinces",
    });
  }
  if (selection.level === "districts") {
    crumbs.push({
      key: "districts",
      label: selection.admin2?.name || "District",
      level: "districts",
    });
  }
  return crumbs;
};

export const SelectionProvider = ({ children }) => {
  const { mapRef, isMapReady } = useMap();
  const { setLayersVisible } = useLayers();
  const {
    selectedCountry,
    selectionLevel,
    setSelectionLevel,
    selectFeature,
    drillUp,
    clearSelection,
    drawMode,
  } = useUI();

  // Refs the once-registered click handler / window listener read so they never
  // close over stale state.
  const levelRef = useRef(selectionLevel);
  const drawModeRef = useRef(drawMode);
  const selectionRef = useRef(selectedCountry);
  // Captured per-layer base filters (the style's own density/pop filter) so the
  // ISO clause is layered on top, never clobbering US-875's filtering.
  const baseFilterRef = useRef({});
  // Bounds remembered per level as the user drills down, so drill-up re-frames
  // without re-querying. iso3→country-label cache mirrors the legacy lookup.
  const boundsRef = useRef({});
  const labelCacheRef = useRef(new Map());
  const initializedRef = useRef(false);
  const hadSelectionRef = useRef(false);
  // The unit currently under the cursor ({ level, id }) — de-dupes hover work so
  // the outline/cursor only update when the hovered unit actually changes.
  const hoverRef = useRef({ level: null, id: null });
  // Per-DMC country bounds warmed at first idle (US-877): lets the header dropdown
  // frame any country later even after drilling elsewhere unloaded its tiles.
  const boundsCacheRef = useRef({});

  useEffect(() => {
    levelRef.current = selectionLevel;
  }, [selectionLevel]);
  useEffect(() => {
    drawModeRef.current = drawMode;
  }, [drawMode]);
  useEffect(() => {
    selectionRef.current = selectedCountry;
  }, [selectedCountry]);

  // Resolve a country's display label from the boundaries source (cached).
  const countryLabel = useCallback((map, iso3) => {
    if (!iso3) return null;
    if (labelCacheRef.current.has(iso3)) return labelCacheRef.current.get(iso3);
    if (!map.querySourceFeatures || !(map.getSource && map.getSource(COUNTRY_SOURCE.id))) {
      return iso3;
    }
    const feats = map.querySourceFeatures(COUNTRY_SOURCE.id, {
      sourceLayer: COUNTRY_SOURCE.sourceLayer,
      filter: ["==", ["get", COUNTRY_SOURCE.isoProp], iso3],
    });
    const props = feats?.[0]?.properties || {};
    const name =
      COUNTRY_SOURCE.nameProps.map((p) => props[p]).find(Boolean) || iso3;
    labelCacheRef.current.set(iso3, name);
    return name;
  }, []);

  // Country bounds fallback when drilling up to a level we have no cached
  // bounds for (e.g. a province was clicked without first selecting the country).
  const countryBounds = useCallback((map, iso3) => {
    if (!map.querySourceFeatures || !(map.getSource && map.getSource(COUNTRY_SOURCE.id))) {
      return null;
    }
    const feats = map.querySourceFeatures(COUNTRY_SOURCE.id, {
      sourceLayer: COUNTRY_SOURCE.sourceLayer,
      filter: ["==", ["get", COUNTRY_SOURCE.isoProp], iso3],
    });
    let bounds = null;
    feats?.forEach((f) => {
      const b = geometryBounds(f.geometry);
      if (!b) return;
      if (!bounds) bounds = b;
      else {
        bounds[0][0] = Math.min(bounds[0][0], b[0][0]);
        bounds[0][1] = Math.min(bounds[0][1], b[0][1]);
        bounds[1][0] = Math.max(bounds[1][0], b[1][0]);
        bounds[1][1] = Math.max(bounds[1][1], b[1][1]);
      }
    });
    return bounds;
  }, []);

  const frameSelection = useCallback(
    (map, selection) => {
      if (!map.fitBounds) return;
      if (!selection) {
        try {
          map.fitBounds(APAC_HOME.bounds, { padding: 24, maxZoom: APAC_HOME.zoom, duration: 600 });
        } catch {
          /* framing is best-effort */
        }
        return;
      }
      const level = selection.level;
      const bounds =
        boundsRef.current[level] ||
        (level === "country" ? countryBounds(map, selection.iso3) : null);
      if (!bounds) return;
      try {
        map.fitBounds(bounds, {
          padding: 40,
          maxZoom: LEVELS[level]?.maxZoom ?? 6.5,
          duration: 600,
        });
      } catch {
        /* framing is best-effort */
      }
    },
    [countryBounds]
  );

  // Re-apply highlight (R-4/R-5) for the selected unit. China + non-DMC never
  // reach here (not selectable); exception countries get no highlight.
  const applyHighlight = useCallback((map, selection) => {
    if (!selection) return;
    const { level, iso3 } = selection;
    if (level === "country") {
      if (!shouldHighlightBorder(iso3)) return;
      LEVELS.country.selectionLayerIds.forEach((id) =>
        safeSetFilter(map, id, ["==", COUNTRY_SOURCE.isoProp, iso3])
      );
      return;
    }
    if (BORDER_EXCEPTION_ISO3.has(iso3)) return;
    const unitId =
      level === "provinces" ? selection.admin1?.id : selection.admin2?.id;
    LEVELS[level].selectionLayerIds.forEach((id) =>
      safeSetFilter(map, id, ["==", PROP_KEYS.id, unitId || ""])
    );
  }, []);

  // The single map-application pass: filters (R-3) + highlight (R-4/R-5) +
  // China sync (R-6), optionally framing the unit. Called by the selection
  // effect and by the visibility-change listener (the latter never frames).
  const applyToMap = useCallback(
    (map, selection, { frame }) => {
      if (!map) return;
      clearHighlights(map);

      const iso3 = selection?.iso3 || null;
      const isoNum = isoNumFor(iso3); // null when no selection or exception

      // Clip a set of VISIBLE layers to the selection by ANDing a country clause
      // onto each layer's own base filter — or restore that base filter when
      // nothing is selected, the layer is hidden, or it's an ADB exception
      // country (`value == null`). Gaps key on the numeric `iso3` property; anchor
      // points key on the alpha-3 `country_code`, so each list clips on its own
      // property/value but shares this pass.
      const clip = (ids, prop, value) => {
        ids.forEach((id) => {
          if (!hasLayer(map, id)) return;
          const base = baseFilterRef.current[id];
          if (value != null && isLayerVisible(map, id)) {
            const clause = ["==", ["get", prop], value];
            safeSetFilter(map, id, base ? ["all", base, clause] : ["all", clause]);
          } else {
            safeSetFilter(map, id, base ?? null);
          }
        });
      };

      clip(ISO_FILTERED_LAYERS, "iso3", isoNum);
      clip(COUNTRY_CODE_FILTERED_LAYERS, "country_code", isoNum != null ? iso3 : null);

      // Country focus veil for the raster layers (US-920 · R-4): same
      // exception rule as the clips above.
      safeSetFilter(
        map,
        FOCUS_MASK_ID,
        isoNum != null ? maskFilterFor(iso3) : MASK_MATCH_NOTHING
      );

      applyHighlight(map, selection);
      syncChinaLayerVisibility(map, iso3);
      if (frame) frameSelection(map, selection);
    },
    [applyHighlight, frameSelection]
  );

  // Programmatic country selection for the header dropdown (US-877 · R-4). Mirrors
  // a country-level map click without a click: resolve the country's bounds (from
  // the idle-warmed cache, else a live query) so the existing frame effect centers
  // it, then set the SAME selection state a click sets. One path, no parallel
  // state; a map click reflects back into the dropdown because both write the one
  // `selectedCountry` (R-5). Ignored for non-DMCs / China (R-6).
  const selectCountry = useCallback(
    (iso3) => {
      if (!isSelectableIso3(iso3)) return;
      const map = mapRef.current;
      if (map) {
        const bounds = boundsCacheRef.current[iso3] || countryBounds(map, iso3);
        if (bounds) boundsRef.current.country = bounds;
        else delete boundsRef.current.country; // don't reuse the previous country's frame
      }
      selectFeature("country", { iso3, name: dmcName(iso3) });
    },
    [mapRef, countryBounds, selectFeature]
  );

  // — One-time map wiring once the map is ready (R-1/R-2/R-10) ————————————————
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady || initializedRef.current) return undefined;
    initializedRef.current = true;

    // Country boundary source + transparent hit target + selection highlights.
    if (map.addSource && map.getSource && !map.getSource(COUNTRY_SOURCE.id)) {
      map.addSource(COUNTRY_SOURCE.id, { type: "vector", url: COUNTRY_SOURCE.url });
    }
    // Focus veil (US-920 · R-4) — created first so every later selection/hover
    // layer stacks above it; starts matching nothing (no selection, no veil).
    ensureLayerUnderLabels(map, {
      id: FOCUS_MASK_ID,
      source: COUNTRY_SOURCE.id,
      "source-layer": COUNTRY_SOURCE.sourceLayer,
      type: "fill",
      paint: { "fill-color": colors.navy, "fill-opacity": 0.45 },
      filter: MASK_MATCH_NOTHING,
    });
    ensureLayerUnderLabels(map, {
      id: LEVELS.country.hitLayerId,
      source: COUNTRY_SOURCE.id,
      "source-layer": COUNTRY_SOURCE.sourceLayer,
      type: "fill",
      paint: { "fill-color": "#000", "fill-opacity": 0 },
    });
    // Hover preview (faint fill + white-cased dashed line), a lighter echo of the
    // selection highlight. Created UNDER the selection layers so a committed
    // selection always reads on top. White casing keeps the outline legible over
    // any gap tile (yellow / navy / red / green).
    ensureLayerUnderLabels(map, {
      id: HOVER.country.fill,
      source: COUNTRY_SOURCE.id,
      "source-layer": COUNTRY_SOURCE.sourceLayer,
      type: "fill",
      paint: { "fill-color": SELECTION_COLOR, "fill-opacity": 0.06 },
      filter: ["==", COUNTRY_SOURCE.isoProp, ""],
    });
    ensureLayerUnderLabels(map, {
      id: HOVER.country.casing,
      source: COUNTRY_SOURCE.id,
      "source-layer": COUNTRY_SOURCE.sourceLayer,
      type: "line",
      paint: { "line-color": "#ffffff", "line-width": 4.5, "line-opacity": 0.9 },
      filter: ["==", COUNTRY_SOURCE.isoProp, ""],
    });
    ensureLayerUnderLabels(map, {
      id: HOVER.country.line,
      source: COUNTRY_SOURCE.id,
      "source-layer": COUNTRY_SOURCE.sourceLayer,
      type: "line",
      paint: {
        "line-color": SELECTION_COLOR,
        "line-width": 2,
        "line-opacity": 1,
        "line-dasharray": [2, 1.5],
      },
      filter: ["==", COUNTRY_SOURCE.isoProp, ""],
    });
    ensureLayerUnderLabels(map, {
      id: "country-selection-fill",
      source: COUNTRY_SOURCE.id,
      "source-layer": COUNTRY_SOURCE.sourceLayer,
      type: "fill",
      paint: { "fill-color": SELECTION_COLOR, "fill-opacity": 0.08 },
      filter: ["==", COUNTRY_SOURCE.isoProp, ""],
    });
    ensureLayerUnderLabels(map, {
      id: "country-selection-line",
      source: COUNTRY_SOURCE.id,
      "source-layer": COUNTRY_SOURCE.sourceLayer,
      type: "line",
      paint: { "line-color": SELECTION_COLOR, "line-width": 2.5, "line-opacity": 0.9 },
      filter: ["==", COUNTRY_SOURCE.isoProp, ""],
    });

    // ADM1 / ADM2 hit + selection layers, cloned from the style's visible admin
    // layers. Absent admin tilesets simply skip — the drill degrades (R-10).
    [
      { level: "provinces", selId: "admin1-selection-line" },
      { level: "districts", selId: "admin2-selection-line" },
    ].forEach(({ level, selId }) => {
      const cfg = LEVELS[level];
      const meta = getLayerMeta(map, cfg.baseLayerId);
      if (!meta) return;
      const hov = HOVER[level];
      ensureLayerUnderLabels(map, {
        id: cfg.hitLayerId,
        source: meta.source,
        "source-layer": meta.sourceLayer,
        type: "fill",
        paint: { "fill-color": "#000", "fill-opacity": 0 },
      });
      // Hover preview line (white-cased dashed), under the selection line.
      ensureLayerUnderLabels(map, {
        id: hov.casing,
        source: meta.source,
        "source-layer": meta.sourceLayer,
        type: "line",
        paint: { "line-color": "#ffffff", "line-width": 4, "line-opacity": 0.9 },
        filter: ["==", PROP_KEYS.id, ""],
      });
      ensureLayerUnderLabels(map, {
        id: hov.line,
        source: meta.source,
        "source-layer": meta.sourceLayer,
        type: "line",
        paint: {
          "line-color": SELECTION_COLOR,
          "line-width": 2,
          "line-opacity": 1,
          "line-dasharray": [2, 1.5],
        },
        filter: ["==", PROP_KEYS.id, ""],
      });
      ensureLayerUnderLabels(map, {
        id: selId,
        source: meta.source,
        "source-layer": meta.sourceLayer,
        type: "line",
        paint: { "line-color": SELECTION_COLOR, "line-width": 2.5, "line-opacity": 0.9 },
        filter: ["==", PROP_KEYS.id, ""],
      });
    });

    // Capture each clippable layer's own filter so the country clause is added,
    // not substituted (preserves US-875 / David's density filtering for gaps and
    // the `infrastructure_type` filter for anchor points).
    [...ISO_FILTERED_LAYERS, ...COUNTRY_CODE_FILTERED_LAYERS].forEach((id) => {
      if (hasLayer(map, id) && map.getFilter) {
        baseFilterRef.current[id] = map.getFilter(id);
      }
    });

    // Click → query the active level's hit layer → derive the unit → select.
    const onClick = (e) => {
      if (drawModeRef.current) return;
      const level = levelRef.current;
      const cfg = LEVELS[level];
      if (!cfg || !hasLayer(map, cfg.hitLayerId)) return;
      if (!e.point || !map.queryRenderedFeatures) return;

      const pad = 2;
      const box = [
        [e.point.x - pad, e.point.y - pad],
        [e.point.x + pad, e.point.y + pad],
      ];
      const hits = map.queryRenderedFeatures(box, { layers: [cfg.hitLayerId] });
      if (!hits || hits.length === 0) return;

      const feature = hits[0];
      const props = feature.properties || {};
      const iso3 =
        level === "country"
          ? props[COUNTRY_SOURCE.isoProp] || null
          : props[PROP_KEYS.iso3] || null;
      if (!isSelectableIso3(iso3)) return; // ignores China + non-DMC (R-6)

      const name = countryLabel(map, iso3);
      const payload = { iso3, name };
      if (level === "provinces") {
        payload.admin1 = { id: props[PROP_KEYS.id] ?? null, name: props[PROP_KEYS.name] ?? null };
      } else if (level === "districts") {
        payload.admin2 = { id: props[PROP_KEYS.id] ?? null, name: props[PROP_KEYS.name] ?? null };
        payload.admin1 = { id: null, name: parentName(map, cfg, feature) };
      }

      // Frame a country from its FULL bounds (idle-warmed cache, else a live
      // source union) — never the clicked feature's geometry. queryRenderedFeatures
      // returns geometry clipped to the on-screen tiles, i.e. only the visible
      // sliver when the country is partly out of view; framing that sliver made
      // each pick of an adjacent country zoom in further instead of reframing the
      // whole country. Mirrors the dropdown path in `selectCountry`. Sub-country
      // levels keep the clicked geometry — they sit inside the already-framed
      // country, so their rendered geometry isn't clipped.
      if (level === "country") {
        const bounds = boundsCacheRef.current[iso3] || countryBounds(map, iso3);
        if (bounds) boundsRef.current.country = bounds;
        else delete boundsRef.current.country;
      } else {
        const bounds = geometryBounds(feature.geometry);
        if (bounds) boundsRef.current[level] = bounds;
      }
      selectFeature(level, payload);
    };
    if (map.on) map.on("click", onClick);

    // Hover → preview the unit under the cursor + drive the cursor affordance.
    // Reuses the click handler's selectability gate so what lights up is exactly
    // what a click will select. De-duped by hovered unit so filter work only runs
    // on change; the cursor is refreshed every move so it never sticks.
    const onMouseMove = (e) => {
      if (drawModeRef.current) {
        if (hoverRef.current.id !== null) clearHoverLayers(map);
        setMapCursor(map, "");
        hoverRef.current = { level: null, id: null };
        return;
      }
      const level = levelRef.current;
      const cfg = LEVELS[level];
      const hov = HOVER[level];
      if (!cfg || !hov || !hasLayer(map, cfg.hitLayerId)) return;
      if (!e.point || !map.queryRenderedFeatures) return;

      const pad = 2;
      const box = [
        [e.point.x - pad, e.point.y - pad],
        [e.point.x + pad, e.point.y + pad],
      ];
      const feature = map.queryRenderedFeatures(box, { layers: [cfg.hitLayerId] })?.[0];

      if (!feature) {
        if (hoverRef.current.id !== null) clearHoverLayers(map);
        setMapCursor(map, "");
        hoverRef.current = { level: null, id: null };
        return;
      }

      const props = feature.properties || {};
      const iso3 =
        level === "country"
          ? props[COUNTRY_SOURCE.isoProp] || null
          : props[PROP_KEYS.iso3] || null;
      const selectable = isSelectableIso3(iso3);
      const featId = level === "country" ? iso3 : props[PROP_KEYS.id] ?? null;

      setMapCursor(map, selectable ? "pointer" : "not-allowed");

      if (hoverRef.current.level === level && hoverRef.current.id === featId) return;
      clearHoverLayers(map);
      hoverRef.current = { level, id: featId };

      // The unit that is ALREADY selected gets no hover preview (Lead UX,
      // 2026-07-16): the selection chrome + focus veil already mark it, and
      // stacking the hover fill on top reads as a rendering glitch. The
      // selected country is exempt at any drill depth; admin units by id.
      const sel = selectionRef.current;
      const isCurrentSelection =
        !!sel &&
        (level === "country"
          ? sel.iso3 === iso3
          : level === "provinces"
            ? sel.admin1?.id != null && sel.admin1.id === featId
            : sel.admin2?.id != null && sel.admin2.id === featId);

      // Outline only selectable, non-exception units (D3/R-5): exception countries
      // keep the pointer cursor but no border. `featId` may repeat across countries
      // only if iso3 repeats, which it does not.
      if (!isCurrentSelection && selectable && shouldHighlightBorder(iso3) && featId != null) {
        const filter = ["==", hov.key, featId];
        if (hov.fill) safeSetFilter(map, hov.fill, filter);
        safeSetFilter(map, hov.casing, filter);
        safeSetFilter(map, hov.line, filter);
      }
    };
    const onMouseOut = () => {
      clearHoverLayers(map);
      setMapCursor(map, "");
      hoverRef.current = { level: null, id: null };
    };
    if (map.on) {
      map.on("mousemove", onMouseMove);
      map.on("mouseout", onMouseOut);
    }

    return () => {
      if (map.off) {
        map.off("click", onClick);
        map.off("mousemove", onMouseMove);
        map.off("mouseout", onMouseOut);
      }
    };
  }, [mapRef, isMapReady, countryLabel, countryBounds, selectFeature]);

  // Apply on every selection change (frames the unit; clears frame to APAC home).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;
    const hadSelection = hadSelectionRef.current;
    if (selectedCountry) hadSelectionRef.current = true;
    applyToMap(map, selectedCountry, { frame: selectedCountry ? true : hadSelection });
    // A unit is usually clicked from its own hover preview, and no mousemove
    // fires while the camera eases / the pointer rests — without this the
    // preview outlives the click and double-tints the fresh selection (the
    // mousemove de-dupe would keep it stale forever).
    clearHoverLayers(map);
    hoverRef.current = { level: null, id: null };
  }, [mapRef, isMapReady, selectedCountry, applyToMap]);

  // Re-apply (no framing) when a layer is toggled — a newly enabled layer
  // inherits the active country filter; China layers re-sync to their base (R-3).
  useEffect(() => {
    if (!isMapReady) return undefined;
    const onVisibilityChange = () => {
      const map = mapRef.current;
      if (map) applyToMap(map, selectionRef.current, { frame: false });
    };
    window.addEventListener("map-layer-visibility-change", onVisibilityChange);
    return () =>
      window.removeEventListener("map-layer-visibility-change", onVisibilityChange);
  }, [mapRef, isMapReady, applyToMap]);

  // Borders follow the active level (US-878 UX consolidation): switching the
  // level shows that tier's admin boundary lines and hides the others, through
  // LayersContext so it stays flicker-free and in lockstep with the map. Lives
  // here (always mounted) so it also fires when a breadcrumb drill-up changes
  // the level while the Map Config panel is on another tab.
  useEffect(() => {
    const active = new Set(boundaryLayersForLevel(selectionLevel));
    const updates = {};
    ALL_BOUNDARY_LAYERS.forEach((id) => {
      updates[id] = active.has(id);
    });
    setLayersVisible(updates);
  }, [selectionLevel, setLayersVisible]);

  // Clear any lingering hover preview when the active level changes — the pointer
  // is usually on the level control (off the map), so no mousemove fires to reset
  // a stale outline from the previous level.
  useEffect(() => {
    const map = mapRef.current;
    if (map && isMapReady) clearHoverLayers(map);
    hoverRef.current = { level: null, id: null };
  }, [selectionLevel, mapRef, isMapReady]);

  // Warm a per-DMC bounds cache once the map first goes idle at the APAC home
  // view, where every DMC's boundary tiles are loaded (US-877). Lets the header
  // dropdown frame any country later, even after drilling elsewhere unloaded its
  // tiles. Best-effort, one-shot; guarded for the test doubles that lack `once`.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady || !map.once) return undefined;
    let cancelled = false;
    const warm = () => {
      if (cancelled) return;
      SELECTABLE_ISO3.forEach((iso3) => {
        if (boundsCacheRef.current[iso3]) return;
        const b = countryBounds(map, iso3);
        if (b) boundsCacheRef.current[iso3] = b;
      });
    };
    map.once("idle", warm);
    return () => {
      cancelled = true;
    };
  }, [mapRef, isMapReady, countryBounds]);

  const crumbs = useMemo(() => buildCrumbs(selectedCountry), [selectedCountry]);

  const value = useMemo(
    () => ({
      selection: selectedCountry,
      selectionLevel,
      levelOrder: LEVEL_ORDER,
      setLevel: setSelectionLevel,
      selectCountry,
      drillUp,
      clearSelection,
      crumbs,
    }),
    [
      selectedCountry,
      selectionLevel,
      setSelectionLevel,
      selectCountry,
      drillUp,
      clearSelection,
      crumbs,
    ]
  );

  return (
    <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>
  );
};

// Read a clicked feature's parent admin name off the parent hit layer (e.g. a
// district's province), querying at the feature's center. Null if unavailable.
function parentName(map, cfg, feature) {
  if (!cfg.parentHitLayerId || !hasLayer(map, cfg.parentHitLayerId)) return null;
  if (!map.project || !map.queryRenderedFeatures) return null;
  const bounds = geometryBounds(feature.geometry);
  if (!bounds) return null;
  const center = {
    lng: (bounds[0][0] + bounds[1][0]) / 2,
    lat: (bounds[0][1] + bounds[1][1]) / 2,
  };
  let px;
  try {
    px = map.project(center);
  } catch {
    return null;
  }
  const pad = 3;
  const box = [
    [px.x - pad, px.y - pad],
    [px.x + pad, px.y + pad],
  ];
  const hit = map.queryRenderedFeatures(box, { layers: [cfg.parentHitLayerId] })?.[0];
  return hit?.properties?.[PROP_KEYS.name] || null;
}
